import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface DatabaseCity {
  id: string;
  name: string;
  slug: string;
  country: string;
  state_province: string | null;
  latitude: number | null;
  longitude: number | null;
  is_active: boolean;
}

interface UseCitiesOptions {
  page?: number;
  pageSize?: number;
  searchTerm?: string;
  country?: string;
}

export const useCitiesFromDatabase = (options: UseCitiesOptions = {}) => {
  const { page = 1, pageSize = 150, searchTerm = '', country = '' } = options;

  return useQuery({
    queryKey: ['cities-database', page, pageSize, searchTerm, country],
    queryFn: async () => {
      let query = supabase
        .from('cities')
        .select('id, name, slug, country, state_province, latitude, longitude, is_active', { count: 'exact' })
        .eq('is_active', true)
        .order('name', { ascending: true });

      // Apply search filter
      if (searchTerm) {
        query = query.or(`name.ilike.%${searchTerm}%,country.ilike.%${searchTerm}%`);
      }

      // Apply country filter
      if (country) {
        query = query.eq('country', country);
      }

      // Apply pagination
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) throw error;

      return {
        cities: data as DatabaseCity[],
        totalCount: count || 0,
        totalPages: Math.ceil((count || 0) / pageSize)
      };
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useAllCitiesCount = () => {
  return useQuery({
    queryKey: ['cities-count'],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('cities')
        .select('*', { count: 'exact', head: true })
        .eq('is_active', true);

      if (error) throw error;
      return count || 0;
    },
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
};
